import { CheckCircle, Warning, XCircle, MagicWand } from "@phosphor-icons/react";

const STATUS_META = {
  pass: { icon: CheckCircle, color: "#ccff00", label: "compliant" },
  warn: { icon: Warning, color: "#f59e0b", label: "warnings" },
  fail: { icon: XCircle, color: "#ef4444", label: "violations" },
};

const SEVERITY_COLOR = {
  high: "text-red-400 border-red-500/30 bg-red-500/5",
  medium: "text-amber-400 border-amber-500/30 bg-amber-500/5",
  low: "text-zinc-400 border-white/10 bg-[#121212]",
};

/**
 * Compact compliance status pill + expanded violations list for one variant.
 * When `onAutoFix` is provided AND the variant has violations, renders a
 * one-click "Auto-fix with AI" button that rewrites headline + primary_text
 * to resolve every violation.
 */
export default function ComplianceBadge({ result, compact = false, onAutoFix, autoFixing = false }) {
  if (!result) return null;
  const meta = STATUS_META[result.status] || STATUS_META.pass;
  const Icon = meta.icon;
  const hasViolations = (result.violations || []).length > 0;

  if (compact) {
    return (
      <span
        data-testid={`compliance-badge-${result.platform}-${result.variant_letter}`}
        className="inline-flex items-center gap-1 px-2 py-1 border font-mono text-[10px] uppercase tracking-wider"
        style={{ borderColor: `${meta.color}55`, color: meta.color }}
        title={`${result.violations.length} finding(s)`}
      >
        <Icon size={11} weight="fill" />
        {meta.label}
      </span>
    );
  }

  return (
    <div
      data-testid={`compliance-badge-${result.platform}-${result.variant_letter}`}
      className="border p-3 space-y-2"
      style={{ borderColor: `${meta.color}44`, backgroundColor: `${meta.color}0a` }}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon size={14} weight="fill" style={{ color: meta.color }} />
          <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: meta.color }}>
            {meta.label}
          </span>
          {result.violations.length > 0 && (
            <span className="font-mono text-[10px] text-zinc-400">
              · {result.violations.length} finding
              {result.violations.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        {hasViolations && onAutoFix && (
          <button
            onClick={onAutoFix}
            disabled={autoFixing}
            data-testid={`auto-fix-${result.platform}-${result.variant_letter}`}
            title="Rewrite this variant with AI to resolve every violation"
            className="flex items-center gap-1 px-2 py-1 bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors"
          >
            <MagicWand size={11} weight={autoFixing ? "regular" : "fill"} className={autoFixing ? "animate-pulse" : ""} />
            {autoFixing ? "fixing…" : "auto-fix with AI"}
          </button>
        )}
      </div>
      {result.violations.map((v, i) => (
        <div
          key={`v-${i}`}
          className={`border px-2 py-2 ${SEVERITY_COLOR[v.severity] || SEVERITY_COLOR.low}`}
        >
          <div className="font-mono text-[10px] uppercase mb-1">
            [{v.severity}] {v.rule}
          </div>
          <div className="text-xs text-zinc-200 italic">
            &ldquo;{v.quoted_text}&rdquo;
          </div>
          {v.suggested_fix && (
            <div className="text-xs text-zinc-400 mt-1">
              → <span className="text-[#ccff00]">{v.suggested_fix}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
